import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import heroBannerImage from '@assets/images/hero-banner.png';
import heroBannerImageDark from '@assets/images/hero-banner-dark.png';
import { useHomeHeroStyles } from './HomeHero.styles';
import { BasicButton, HeroBanner } from '@/components';
import { useTheme } from '@/theme';

export interface HomeHeroProps {
  onStartBooking?: () => void;
  /**
   * Opt-in to the Figma 2026 landing hero (node `1-1429`): soft canvas card,
   * eyebrow label, primary CTA, and an inline tip card. Defaults to `false`
   * so existing usages render the legacy gradient hero unchanged.
   */
  figmaVariant?: boolean;
  /**
   * Optional ref attached to a wrapper around the primary CTA. Consumers use
   * this to drive viewport-aware features (e.g., the floating action bar on
   * the home screen — FR-013).
   */
  ctaRef?: Ref<HTMLSpanElement>;
}

export const HomeHero = ({ onStartBooking, figmaVariant = false, ctaRef }: HomeHeroProps) => {
  const styles = useHomeHeroStyles();
  const { t } = useTranslation('homeScreen');
  const { isDarkMode } = useTheme();
  // Swap to the dark night artwork in dark mode (spec 101); the transparent
  // scrim token lets the photo show through unaltered.
  const heroImage = isDarkMode ? heroBannerImageDark : heroBannerImage;

  if (figmaVariant) {
    return (
      <HeroBanner
        figmaVariant
        homeAlignedContent
        backgroundImage={heroImage}
        eyebrow={t('homeScreen.hero.eyebrow')}
        title={t('homeScreen.hero.title')}
        subtitle={t('homeScreen.hero.description')}
        actions={
          <span ref={ctaRef} data-testid="home-hero-cta">
            <BasicButton
              variant="primary"
              figmaVariant
              size="large"
              onClick={onStartBooking}
              className={styles.bookButton}
            >
              <span className={styles.plusGlyph}>+</span>
              {t('homeScreen.hero.button')}
            </BasicButton>
          </span>
        }
      />
    );
  }

  return (
    <div
      className={styles.root}
      style={{ backgroundImage: `url(${heroBannerImage})` }}
      role="banner"
    >
      <div className={styles.overlay}>
        <p className={styles.title} role="heading" aria-level={2}>
          {t('homeScreen.hero.title')}
        </p>

        <p className={styles.description}>{t('homeScreen.hero.description')}</p>

        <span ref={ctaRef}>
          <BasicButton
            variant="secondary"
            size="large"
            className={styles.button}
            onClick={onStartBooking}
          >
            <span className={styles.plusGlyph}>+</span>
            {t('homeScreen.hero.button')}
          </BasicButton>
        </span>
      </div>
    </div>
  );
};

HomeHero.displayName = 'HomeHero';
